import { ArrowRightIcon, InfoCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Button, Callout, Card, TextField } from '@radix-ui/themes';
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scorePath, useTeams } from '../api';
import SearchableTeamSelect from './SearchableTeamSelect';

interface Props {
  higher?: string;
  lower?: string;
  teamId?: number | null;
  onTeamChange?: (teamId: number | null) => void;
}

export default function ScoreSearchForm({ higher = '', lower = '', teamId = null, onTeamChange }: Props) {
  const navigate = useNavigate();
  const { teams, unavailable } = useTeams();
  const [higherScore, setHigherScore] = useState(higher);
  const [lowerScore, setLowerScore] = useState(lower);
  const [localTeamId, setLocalTeamId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const selectedTeam = onTeamChange ? teamId : localTeamId;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const winner = Number(higherScore);
    const loser = Number(lowerScore);
    const scores = [higherScore, lowerScore];
    if (scores.some(score => !/^\d+$/.test(score) || !Number.isSafeInteger(Number(score)))) {
      setError('Enter a whole number of zero or more for each score.');
    } else if (winner < loser) {
      setError('The higher score must be greater than or equal to the lower score.');
    } else {
      setError('');
      navigate(scorePath(winner, loser, selectedTeam));
    }
  }

  return (
    <Card className="lookup-card">
      <div className="lookup-card-heading">
        <span className="icon-tile"><MagnifyingGlassIcon width="20" height="20" /></span>
        <div>
          <h2>{higher ? 'Refine your search' : 'Find a final score'}</h2>
          <p>{higher ? 'Change the score or narrow the results by team.' : 'Enter the two scores from the final whistle.'}</p>
        </div>
      </div>
      <form className="lookup-form" onSubmit={handleSubmit} noValidate>
        <div className="score-fields">
          <div>
            <label className="field-label" htmlFor="higher-score">Higher score</label>
            <TextField.Root id="higher-score" className="score-field" size="3" inputMode="numeric" required placeholder="31"
              value={higherScore} aria-invalid={Boolean(error)} aria-describedby={error ? 'score-error' : undefined}
              onChange={event => { setHigherScore(event.target.value); setError(''); }} />
          </div>
          <span className="score-field-separator" aria-hidden="true">–</span>
          <div>
            <label className="field-label" htmlFor="lower-score">Lower score</label>
            <TextField.Root id="lower-score" className="score-field" size="3" inputMode="numeric" required placeholder="28"
              value={lowerScore} aria-invalid={Boolean(error)} aria-describedby={error ? 'score-error' : undefined}
              onChange={event => { setLowerScore(event.target.value); setError(''); }} />
          </div>
        </div>
        <SearchableTeamSelect
          label="Team (optional)"
          teams={teams}
          unavailable={unavailable}
          selectedTeam={selectedTeam}
          onTeamChange={onTeamChange ?? setLocalTeamId}
        />
        {error && (
          <Callout.Root id="score-error" color="red" role="alert">
            <Callout.Icon><InfoCircledIcon /></Callout.Icon>
            <Callout.Text>{error}</Callout.Text>
          </Callout.Root>
        )}
        <Button size="3" type="submit" className="action-primary lookup-submit">Search games <ArrowRightIcon /></Button>
      </form>
    </Card>
  );
}
